import { Box, Tab, Tabs } from "@mui/material";
import React from "react";
import { PersistedTabPanel } from "../../../../../../../common/PersistedTabPanel";
import type { DrillDownInfo, MeasureDrillDownInfo } from "../../../../../../../../api/types/svTypes.ts";
import { MeasureUnitTable } from "../../../../../../../../api/types/svTypes.ts";
import type { DrillDownPage } from "../../../../store/types/drillDownPageState.types";
import ChartOfAccountsPart from "./chartOfAccounts/ChartOfAccountsPart";
import { drillDownTabSx, drillDownTabsSx } from "./constants";
import { isMeasureDrillDownInfo } from "./isMeasureDrillDownInfo";
import LedgerPart from "./ledger/LedgerPart";
import { getLedgerTitle } from "./utils/getLedgerTitle";

enum MainPageTab {
  ChartOfAccounts = "chartOfAccounts",
  GeneralLedger = "gl",
  MemoLedger = "memogl",
}

interface Props {
  configuration: DrillDownPage;
  info: DrillDownInfo;
}

export default function MainPage({ configuration, info }: Props) {
  const glInfo = info.items.find(
    (item): item is MeasureDrillDownInfo => isMeasureDrillDownInfo(item) && item.table === MeasureUnitTable.Gl
  );
  const memoInfo = info.items.find(
    (item): item is MeasureDrillDownInfo => isMeasureDrillDownInfo(item) && item.table === MeasureUnitTable.Memo
  );
  const [selectedTab, setSelectedTab] = React.useState<MainPageTab>(() => {
    if (configuration.chartOfAccounts) {
      return MainPageTab.ChartOfAccounts;
    }
    if (glInfo) {
      return MainPageTab.GeneralLedger;
    }
    if (memoInfo) {
      return MainPageTab.MemoLedger;
    }
    return MainPageTab.ChartOfAccounts;
  });

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Tabs value={selectedTab} sx={drillDownTabsSx} onChange={(_, value: MainPageTab) => setSelectedTab(value)}>
        {configuration.chartOfAccounts && (
          <Tab
            key={MainPageTab.ChartOfAccounts}
            label="Chart of Accounts"
            value={MainPageTab.ChartOfAccounts}
            sx={drillDownTabSx}
          />
        )}
        {glInfo && (
          <Tab
            key={MainPageTab.GeneralLedger}
            label={getLedgerTitle(glInfo)}
            value={MainPageTab.GeneralLedger}
            sx={drillDownTabSx}
          />
        )}
        {memoInfo && (
          <Tab
            key={MainPageTab.MemoLedger}
            label={getLedgerTitle(memoInfo)}
            value={MainPageTab.MemoLedger}
            sx={drillDownTabSx}
          />
        )}
      </Tabs>
      {configuration.chartOfAccounts && (
        <PersistedTabPanel
          key={MainPageTab.ChartOfAccounts}
          value={MainPageTab.ChartOfAccounts}
          selectedTab={selectedTab}
          sx={{
            height: "100%",
            minHeight: 0,
          }}
        >
          <ChartOfAccountsPart configuration={configuration} info={info} />
        </PersistedTabPanel>
      )}
      {glInfo && (
        <PersistedTabPanel
          key={MainPageTab.GeneralLedger}
          value={MainPageTab.GeneralLedger}
          selectedTab={selectedTab}
          sx={{
            height: "100%",
            minHeight: 0,
          }}
        >
          <LedgerPart configuration={configuration} info={glInfo} />
        </PersistedTabPanel>
      )}
      {memoInfo && (
        <PersistedTabPanel
          key={MainPageTab.MemoLedger}
          value={MainPageTab.MemoLedger}
          selectedTab={selectedTab}
          sx={{
            height: "100%",
            minHeight: 0,
          }}
        >
          <LedgerPart configuration={configuration} info={memoInfo} />
        </PersistedTabPanel>
      )}
    </Box>
  );
}
